import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  FlaskConical,
  Plus,
  Play,
  Pause,
  Square,
  Archive,
  ChevronRight,
  Users,
  TrendingUp,
  BarChart3,
  Settings,
  Loader2,
  Check,
  X,
  Edit,
  Trash2,
  RefreshCw
} from "lucide-react";

const FEATURE_AREAS = [
  { id: "job_recommendations", name: "Job Recommendations" },
  { id: "subscription_page", name: "Subscription Page UI" },
  { id: "email_notifications", name: "Email Notifications" },
  { id: "ai_interviews", name: "AI Interviews" },
  { id: "search_results", name: "Search Results" },
  { id: "onboarding", name: "Onboarding Flow" },
  { id: "general", name: "General" }
];

const AdminExperiments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchExperiments();
  }, [user]);

  const fetchExperiments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/experiments/`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setExperiments(data.experiments);
      }
    } catch (error) {
      toast.error("Failed to load experiments");
    } finally {
      setLoading(false);
    }
  };

  const startExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API}/experiments/${experimentId}/start`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Experiment started");
        fetchExperiments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start experiment");
      }
    } catch (error) {
      toast.error("Failed to start experiment");
    }
  };

  const pauseExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API}/experiments/${experimentId}/pause`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Experiment paused");
        fetchExperiments();
      }
    } catch (error) {
      toast.error("Failed to pause experiment");
    }
  };

  const stopExperiment = async (experimentId) => {
    if (!confirm("Are you sure you want to stop this experiment? This action cannot be undone.")) {
      return;
    }
    try {
      const response = await fetch(`${API}/experiments/${experimentId}/stop`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Experiment stopped");
        fetchExperiments();
      }
    } catch (error) {
      toast.error("Failed to stop experiment");
    }
  };

  const archiveExperiment = async (experimentId) => {
    if (!confirm("Are you sure you want to archive this experiment?")) {
      return;
    }
    try {
      const response = await fetch(`${API}/experiments/${experimentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Experiment archived");
        fetchExperiments();
      }
    } catch (error) {
      toast.error("Failed to archive experiment");
    }
  };

  const viewResults = async (experiment) => {
    setSelectedExperiment(experiment);
    setShowResultsModal(true);
    
    try {
      const response = await fetch(`${API}/experiments/${experiment.experiment_id}/results`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      toast.error("Failed to load results");
    }
  };

  const filteredExperiments = experiments.filter(exp => {
    if (filter === "all") return true;
    return exp.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/20 text-gray-400",
      running: "bg-green-500/20 text-green-400",
      paused: "bg-yellow-500/20 text-yellow-400",
      completed: "bg-blue-500/20 text-blue-400",
      archived: "bg-red-500/20 text-red-400"
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase ${styles[status] || styles.draft}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-crimson animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                A/B <span className="text-crimson">TESTING</span>
              </h1>
              <p className="text-gray-400">Manage experiments and analyze results</p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchExperiments} variant="outline" className="border-[#333] text-white">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button 
                onClick={() => setShowCreateModal(true)} 
                className="bg-crimson hover:bg-crimson-hover"
                data-testid="create-experiment-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Experiment
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {["all", "draft", "running", "paused", "completed"].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  filter === status
                    ? "bg-crimson text-white"
                    : "bg-obsidian-light text-gray-400 hover:text-white"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard 
              icon={FlaskConical} 
              label="Total Experiments" 
              value={experiments.length} 
            />
            <StatCard 
              icon={Play} 
              label="Running" 
              value={experiments.filter(e => e.status === "running").length}
              color="text-green-400"
            />
            <StatCard 
              icon={Pause} 
              label="Paused" 
              value={experiments.filter(e => e.status === "paused").length}
              color="text-yellow-400"
            />
            <StatCard 
              icon={Check} 
              label="Completed" 
              value={experiments.filter(e => e.status === "completed").length}
              color="text-blue-400"
            />
          </div>

          {/* Experiments List */}
          {filteredExperiments.length === 0 ? (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">No experiments found</h3>
              <p className="text-gray-500 mb-4">Create your first A/B test to start experimenting</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-crimson hover:bg-crimson-hover">
                <Plus className="w-4 h-4 mr-2" /> Create Experiment
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredExperiments.map((experiment) => (
                <motion.div
                  key={experiment.experiment_id}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-[#333] transition-colors"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-lg font-bold text-white">
                          {experiment.name}
                        </h3>
                        {getStatusBadge(experiment.status)}
                      </div>
                      <p className="text-gray-400 text-sm mb-3">{experiment.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-500">
                          <Settings className="w-4 h-4" />
                          <span>{experiment.feature_area}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>{experiment.variants?.length || 0} variants</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <TrendingUp className="w-4 h-4" />
                          <span>{experiment.traffic_percentage}% traffic</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <BarChart3 className="w-4 h-4" />
                          <span>{experiment.target_metric}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {experiment.status === "draft" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => startExperiment(experiment.experiment_id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`start-${experiment.experiment_id}`}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#333] text-white"
                            onClick={() => {
                              setSelectedExperiment(experiment);
                              setShowCreateModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {experiment.status === "running" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => pauseExperiment(experiment.experiment_id)}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => stopExperiment(experiment.experiment_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Square className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {experiment.status === "paused" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => startExperiment(experiment.experiment_id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => stopExperiment(experiment.experiment_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Square className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#333] text-white"
                        onClick={() => viewResults(experiment)}
                        data-testid={`results-${experiment.experiment_id}`}
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      {experiment.status !== "running" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400"
                          onClick={() => archiveExperiment(experiment.experiment_id)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Variants Preview */}
                  <div className="mt-4 pt-4 border-t border-[#333]">
                    <div className="flex flex-wrap gap-2">
                      {experiment.variants?.map((variant, i) => (
                        <div
                          key={i}
                          className="px-3 py-1 bg-obsidian border border-[#333] text-sm"
                        >
                          <span className="text-gray-400">{variant.name}</span>
                          <span className="text-gray-600 ml-2">({variant.weight}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateExperimentModal
          experiment={selectedExperiment}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedExperiment(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedExperiment(null);
            fetchExperiments();
          }}
        />
      )}

      {/* Results Modal */}
      {showResultsModal && selectedExperiment && (
        <ResultsModal
          experiment={selectedExperiment}
          results={results}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedExperiment(null);
            setResults(null);
          }}
        />
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = "text-crimson" }) => (
  <div className="bg-obsidian-light border border-[#222] p-4">
    <div className="flex items-center gap-2 text-gray-400 mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
  </div>
);

const CreateExperimentModal = ({ experiment, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: experiment?.name || "",
    description: experiment?.description || "",
    feature_area: experiment?.feature_area || "general",
    target_metric: experiment?.target_metric || "conversion",
    traffic_percentage: experiment?.traffic_percentage || 100,
    variants: experiment?.variants || [
      { name: "control", description: "Original version", weight: 50, config: {} },
      { name: "treatment", description: "Test version", weight: 50, config: {} }
    ]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = experiment
        ? `${API}/experiments/${experiment.experiment_id}`
        : `${API}/experiments/`;
      
      const response = await fetch(url, {
        method: experiment ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(experiment ? "Experiment updated" : "Experiment created");
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save experiment");
      }
    } catch (error) {
      toast.error("Failed to save experiment");
    } finally {
      setLoading(false);
    }
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const addVariant = () => {
    const newWeight = Math.floor(100 / (formData.variants.length + 1));
    const newVariants = formData.variants.map(v => ({ ...v, weight: newWeight }));
    newVariants.push({
      name: `variant_${formData.variants.length}`,
      description: "",
      weight: 100 - (newWeight * formData.variants.length),
      config: {}
    });
    setFormData({ ...formData, variants: newVariants });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 2) {
      toast.error("Minimum 2 variants required");
      return;
    }
    const newVariants = formData.variants.filter((_, i) => i !== index);
    // Redistribute weights
    const totalWeight = newVariants.reduce((sum, v) => sum + v.weight, 0);
    const diff = 100 - totalWeight;
    if (diff !== 0 && newVariants.length > 0) {
      newVariants[0].weight += diff;
    }
    setFormData({ ...formData, variants: newVariants });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-16 lg:pt-4 overflow-y-auto">
      <motion.div
        className="bg-obsidian-light border border-[#222] max-w-2xl w-full p-4 sm:p-6 my-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <h2 className="font-heading text-lg sm:text-xl font-bold mb-4 sm:mb-6">
          {experiment ? "Edit Experiment" : "Create New Experiment"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-obsidian border-[#333] text-white"
              placeholder="e.g., Job Recommendation Algorithm v2"
              required
              data-testid="experiment-name"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-obsidian border-[#333] text-white"
              placeholder="What are you testing?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Feature Area</label>
              <select
                value={formData.feature_area}
                onChange={(e) => setFormData({ ...formData, feature_area: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white"
              >
                {FEATURE_AREAS.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Target Metric</label>
              <select
                value={formData.target_metric}
                onChange={(e) => setFormData({ ...formData, target_metric: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white"
              >
                <option value="conversion">Conversion Rate</option>
                <option value="engagement">Engagement</option>
                <option value="retention">Retention</option>
                <option value="revenue">Revenue</option>
                <option value="click_through">Click-Through Rate</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">
              Traffic Percentage: {formData.traffic_percentage}%
            </label>
            <input
              type="range"
              min="1"
              max="100"
              value={formData.traffic_percentage}
              onChange={(e) => setFormData({ ...formData, traffic_percentage: parseInt(e.target.value) })}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Percentage of users who will be enrolled in this experiment
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-400">Variants</label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-[#333] text-white"
                onClick={addVariant}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Variant
              </Button>
            </div>
            <div className="space-y-3">
              {formData.variants.map((variant, index) => (
                <div key={index} className="p-3 bg-obsidian border border-[#333]">
                  <div className="flex items-center justify-between mb-2">
                    <Input
                      value={variant.name}
                      onChange={(e) => updateVariant(index, "name", e.target.value)}
                      className="bg-obsidian-light border-[#444] text-white w-40"
                      placeholder="Variant name"
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={variant.weight}
                        onChange={(e) => updateVariant(index, "weight", parseInt(e.target.value) || 0)}
                        className="bg-obsidian-light border-[#444] text-white w-20 text-center"
                      />
                      <span className="text-gray-500">%</span>
                      {formData.variants.length > 2 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-400"
                          onClick={() => removeVariant(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <Input
                    value={variant.description}
                    onChange={(e) => updateVariant(index, "description", e.target.value)}
                    className="bg-obsidian-light border-[#444] text-white text-sm"
                    placeholder="Description (optional)"
                  />
                </div>
              ))}
            </div>
            {formData.variants.reduce((sum, v) => sum + v.weight, 0) !== 100 && (
              <p className="text-red-400 text-sm mt-2">
                Variant weights must sum to 100%
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-[#333] text-white"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || formData.variants.reduce((sum, v) => sum + v.weight, 0) !== 100}
              className="flex-1 bg-crimson hover:bg-crimson-hover"
              data-testid="save-experiment-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : experiment ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const ResultsModal = ({ experiment, results, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-16 lg:pt-4 overflow-y-auto">
      <motion.div
        className="bg-obsidian-light border border-[#222] max-w-3xl w-full p-4 sm:p-6 my-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold">
            Results: {experiment.name}
          </h2>
          <Button variant="ghost" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {!results ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-crimson animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-obsidian p-4 border border-[#333]">
                <div className="text-sm text-gray-400 mb-1">Total Participants</div>
                <div className="text-2xl font-bold text-white">{results.total_participants}</div>
              </div>
              <div className="bg-obsidian p-4 border border-[#333]">
                <div className="text-sm text-gray-400 mb-1">Total Conversions</div>
                <div className="text-2xl font-bold text-green-400">{results.total_conversions}</div>
              </div>
              <div className="bg-obsidian p-4 border border-[#333]">
                <div className="text-sm text-gray-400 mb-1">Overall Rate</div>
                <div className="text-2xl font-bold text-white">
                  {results.total_participants > 0 
                    ? ((results.total_conversions / results.total_participants) * 100).toFixed(2)
                    : 0}%
                </div>
              </div>
            </div>

            {/* Statistical Significance Section */}
            {results.statistical_analysis && (
              <div className={`p-4 border ${
                results.statistical_analysis.is_significant 
                  ? "bg-green-500/10 border-green-500/30" 
                  : "bg-yellow-500/10 border-yellow-500/30"
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                    results.statistical_analysis.is_significant 
                      ? "bg-green-500/20" 
                      : "bg-yellow-500/20"
                  }`}>
                    {results.statistical_analysis.is_significant ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <TrendingUp className="w-5 h-5 text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-white">
                        {results.statistical_analysis.is_significant 
                          ? "Statistically Significant" 
                          : "Not Yet Significant"}
                      </span>
                      {results.statistical_analysis.confidence_level > 0 && (
                        <span className={`px-2 py-0.5 text-xs ${
                          results.statistical_analysis.confidence_level >= 99 
                            ? "bg-green-500/20 text-green-400"
                            : results.statistical_analysis.confidence_level >= 95
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-yellow-500/20 text-yellow-400"
                        }`}>
                          {results.statistical_analysis.confidence_level}% Confidence
                        </span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-2">
                      {results.statistical_analysis.recommendation}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                      <span>P-value: {results.statistical_analysis.p_value}</span>
                      <span>Lift: {results.statistical_analysis.lift > 0 ? "+" : ""}{results.statistical_analysis.lift}%</span>
                      <span>Min. sample needed: {results.statistical_analysis.minimum_sample_needed}/variant</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Variant Breakdown */}
            <div>
              <h3 className="text-lg font-bold text-white mb-3">Variant Performance</h3>
              <div className="space-y-3">
                {Object.entries(results.variant_stats || {}).map(([variant, stats]) => {
                  const isWinner = results.winner?.variant === variant;
                  return (
                    <div
                      key={variant}
                      className={`p-4 bg-obsidian border ${isWinner ? "border-green-500" : "border-[#333]"}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">{variant}</span>
                          {isWinner && results.statistical_analysis?.is_significant && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs">
                              WINNER
                            </span>
                          )}
                          {isWinner && !results.statistical_analysis?.is_significant && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs">
                              LEADING
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-crimson">
                            {stats.conversion_rate}%
                          </span>
                          {stats.confidence_interval && (
                            <div className="text-xs text-gray-500">
                              CI: [{stats.confidence_interval.lower}% - {stats.confidence_interval.upper}%]
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Participants:</span>
                          <span className="text-white ml-2">{stats.participants}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Conversions:</span>
                          <span className="text-green-400 ml-2">{stats.conversions}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Unique:</span>
                          <span className="text-white ml-2">{stats.unique_conversions}</span>
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="mt-3 h-2 bg-obsidian-light rounded-full overflow-hidden">
                        <div
                          className="h-full bg-crimson"
                          style={{ width: `${stats.conversion_rate}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {results.total_participants === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No participants yet. Start the experiment to collect data.</p>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminExperiments;
